import { Component, Inject, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Package, ProductInterface } from '@vendasta/marketplace-packages';
import { SubscriptionList } from '@vendasta/rx-utils';
import { Pipeline } from '@vendasta/sales-common';
import { StageInterface } from '@vendasta/sales-opportunities';
import { DateRange } from '@vendasta/uikit';
import { addWeeks, addYears, startOfMonth } from 'date-fns';
import { Observable, ReplaySubject, combineLatest, firstValueFrom } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import {
  ActualCloseFilterService,
  AssignedDateFilterService,
  CreatedDateFilterService,
  DateFilterServiceInterface,
  ExpectedCloseFilterService,
} from '../../filters/date-filters/date-filters.service';
import {
  DateRangeSelectorDialogComponent,
  DateRangeSelectorDialogData,
} from '../../filters/date-range-selector-dialog/date-range-selector-dialog.component';
import { SalesTeamOption } from '../../filters/filter-options/sales-team-option.model';
import { SalespersonOption } from '../../filters/filter-options/salesperson-option.model';
import { OpportunityTypeFilterComponent } from '../../filters/opportunity-type-filter/opportunity-type-filter.component';
import { OpportunityTypeFilterService } from '../../filters/opportunity-type-filter/opportunity-type-filter.service';
import { PipelineFilterPackageSelectionService } from '../../filters/package-selector/pipeline-filter-package-selection.service';
import { PipelineFilterPackageSelectorComponent } from '../../filters/package-selector/pipeline-filter-package-selector.component';
import {
  PipelineDialogData,
  PipelineMultiSelectComponent,
} from '../../filters/pipeline-multi-select/pipeline-multi-select.component';
import { PipelineMultiSelectService } from '../../filters/pipeline-multi-select/pipeline-multi-select.service';
import { ProductSelectorComponent } from '../../filters/product-selector/product-selector.component';
import { ProductSelectorService } from '../../filters/product-selector/product-selector.service';
import { SalespersonMultiSelectComponent } from '../../filters/salesperson-multi-select/salesperson-multi-select.component';
import { SalespersonMultiSelectStoreService } from '../../filters/salesperson-multi-select/salesperson-multi-select.store.service';
import { SalesTeamMultiSelectStoreService } from '../../filters/salesteam-multi-select/sales-team-multi-select-store.service';
import { SalesTeamMultiSelectComponent } from '../../filters/salesteam-multi-select/sales-team-multi-select.component';
import { TagMultiSelectionStoreService } from '../../filters/tag-selector/tag-multi-selection.service';
import { TagMultiSelectComponent } from '../../filters/tag-selector/tag-multi-selector.component';
import { PipelineStatusCheckboxComponent } from '../../pipelines/pipeline-status-checkbox/pipeline-status-checkbox.component';
import {
  PipelineStatusCheckboxService,
  PipelineStatuses,
} from '../../pipelines/pipeline-status-checkbox/pipeline-status-checkbox.service';
import {
  HAS_ACCESS_TO_ALL_ACCOUNTS_IN_MARKET_TOKEN,
  SALES_TEAM_SERVICE_TOKEN,
  SalesTeamServiceInterface,
} from '../tokens';
import { AccountFilterComponent } from '../../filters/account-filter/account-filter.component';
import { AccountFilterService } from '../../filters/account-filter/account-filter.service';

@Component({
  selector: 'sales-ui-pipeline-table-filter-chips',
  templateUrl: './pipeline-table-filter-chips.component.html',
  styleUrls: ['./pipeline-table-filter-chips.component.scss'],
  standalone: false,
})
export class PipelineTableFilterChipsComponent implements OnDestroy {
  selectedSalespeopleNames$: Observable<string>;
  selectedSalesTeamNames$: Observable<string>;
  showSalespeopleAndTeams$: Observable<boolean>;
  readonly pipelinesSelectedFilter$: Observable<Pipeline[]> = this.pipelineMultiSelectService.selectedPipelines$.pipe(
    startWith([]),
    shareReplay(1),
  );
  readonly pipelineStageSelectedFilter$: Observable<StageInterface> =
    this.pipelineMultiSelectService.selectedPipelineStage$.pipe(shareReplay(1));
  readonly selectedPipelineNames$: Observable<string>;
  readonly expectedCloseFilter$: Observable<DateRange> = this.expectedCloseFilterService.dateRange$.pipe(
    startWith(<DateRange>null),
    shareReplay(1),
  );

  readonly selectedTypes$ = this.opportunityTypeFilterService.selectedTypes$;
  readonly selectedTypeNames$ = this.opportunityTypeFilterService.selectedTypeNames$;

  readonly expectedCloseFilterToString$: Observable<string> = this.expectedCloseFilterService.dateFilterToString$;
  readonly createdDateFilter$: Observable<DateRange> = this.createdDateFilterService.dateRange$.pipe(
    startWith(<DateRange>null),
    shareReplay(1),
  );
  readonly createdDateFilterToString$: Observable<string> = this.createdDateFilterService.dateFilterToString$;
  readonly actualCloseFilter$: Observable<DateRange> = this.actualCloseFilterService.dateRange$.pipe(
    startWith(<DateRange>null),
    shareReplay(1),
  );
  readonly actualCloseFilterToString$: Observable<string> = this.actualCloseFilterService.dateFilterToString$;
  readonly assignedDateFilter$: Observable<DateRange> = this.assignedDateFilterService.dateRange$.pipe(
    startWith(<DateRange>null),
    shareReplay(1),
  );
  readonly assignedDateFilterToString$: Observable<string> = this.assignedDateFilterService.dateFilterToString$;
  readonly selectedPackages$: Observable<Package[]>;
  readonly selectedPackagesNames$: Observable<string>;
  readonly selectedProducts$: Observable<ProductInterface[]>;
  readonly selectedProductNames$: Observable<string>;
  readonly selectedStatusesNames$: Observable<string>;
  readonly selectedAccountGroupName$ = this.accountFilterService.selectedAccountGroupName$;

  private readonly subscriptions = SubscriptionList.new();
  private readonly today = new Date();
  private readonly CALENDAR_DEFAULTS = {
    NOW: this.today,
    ADD_ONE_WEEK: addWeeks(this.today, 1),
    ADD_TWO_YEARS: addYears(this.today, 2),
    START_OF_MONTH: startOfMonth(this.today),
  };
  private readonly selectedTags$$ = new ReplaySubject<string[]>(1);
  readonly selectedTags$: Observable<string[]> = this.selectedTags$$.asObservable();
  private readonly selectedStatuses$: Observable<PipelineStatuses> = this.statusesFilterStore.statuses$;
  readonly hasSelectedStatuses$: Observable<boolean> = this.selectedStatuses$.pipe(
    map((s: PipelineStatuses): boolean => s?.won || s?.lost || s?.open),
  );

  constructor(
    private readonly dialog: MatDialog,
    private readonly spStore: SalespersonMultiSelectStoreService,
    private readonly tagStore: TagMultiSelectionStoreService,
    private readonly stStore: SalesTeamMultiSelectStoreService,
    private readonly pkgStore: PipelineFilterPackageSelectionService,
    private readonly productSelectorService: ProductSelectorService,
    private readonly pipelineMultiSelectService: PipelineMultiSelectService,
    private readonly statusesFilterStore: PipelineStatusCheckboxService,
    private readonly expectedCloseFilterService: ExpectedCloseFilterService,
    private readonly actualCloseFilterService: ActualCloseFilterService,
    private readonly createdDateFilterService: CreatedDateFilterService,
    private readonly assignedDateFilterService: AssignedDateFilterService,
    private readonly opportunityTypeFilterService: OpportunityTypeFilterService,
    private readonly accountFilterService: AccountFilterService,
    private readonly i18n: TranslateService,
    @Inject(SALES_TEAM_SERVICE_TOKEN) private salesTeamsService: SalesTeamServiceInterface,
    @Inject(HAS_ACCESS_TO_ALL_ACCOUNTS_IN_MARKET_TOKEN) private hasAccessToAllAccountsInMarket$: Observable<boolean>,
  ) {
    this.subscriptions.add(this.stStore.selectedSalesTeams$.pipe(map((st) => st.map((s) => s.id))), (ids) =>
      this.salesTeamsService.salesTeamIdsToArrayOfSalesPeopleIds(ids),
    );

    this.selectedSalespeopleNames$ = this.spStore.selectedSalespeople$.pipe(
      map((sp: SalespersonOption[]): string => sp.map((s) => s.fullName).join(', ')),
    );

    this.selectedPipelineNames$ = this.pipelinesSelectedFilter$.pipe(
      filter((p) => Boolean(p)),
      map((pipelines) => pipelines.map((p) => p.name).join(', ')),
    );

    this.selectedSalesTeamNames$ = this.stStore.selectedSalesTeams$.pipe(
      map((sp: SalesTeamOption[]): string => sp.map((s) => s.teamName).join(', ')),
    );
    this.showSalespeopleAndTeams$ = this.hasAccessToAllAccountsInMarket$;
    this.subscriptions.add(
      combineLatest([this.tagStore.selectedOppTags$, this.tagStore.selectedAccountTags$]),
      ([oppTags, accTags]) => {
        if ((oppTags || []).length === 0 && (accTags || []).length === 0) {
          this.selectedTags$$.next(null);
        } else {
          this.selectedTags$$.next(oppTags.concat(accTags).map((t) => t.name));
        }
      },
    );
    this.selectedPackages$ = this.pkgStore.selectedPackages$;
    this.selectedPackagesNames$ = this.selectedPackages$.pipe(
      filter((p) => Boolean(p)),
      map((packages) => packages.map((p) => p.name).join(', ')),
    );

    this.selectedProducts$ = this.productSelectorService.selectedProducts$;
    this.selectedProductNames$ = this.productSelectorService.selectedProducts$.pipe(
      map((products) => {
        return products.map((p) => p.name).join(', ');
      }),
    );

    this.selectedStatusesNames$ = this.buildStatusFitlerChipNames();
  }

  ngOnDestroy(): void {
    this.subscriptions.destroy();
  }

  openSalespeopleSelector(): void {
    this.dialog.open(SalespersonMultiSelectComponent, { width: '400px' });
  }

  openAccountGroupFilter(): void {
    this.dialog.open(AccountFilterComponent, { width: '400px' });
  }

  openOpportunityTypeFilter(): void {
    this.dialog.open(OpportunityTypeFilterComponent, { width: '400px' });
  }

  async openPipelineSelector(): Promise<void> {
    const pipelines = await firstValueFrom(this.pipelinesSelectedFilter$);
    const pipelineStage = await firstValueFrom(this.pipelineStageSelectedFilter$);
    this.dialog.open(PipelineMultiSelectComponent, {
      width: '400px',
      data: <PipelineDialogData>{ pipelines: pipelines, pipelineStage: pipelineStage },
    });
  }

  async openExpectedCloseSelector(): Promise<void> {
    const expectedCloseFilter = await firstValueFrom(this.expectedCloseFilter$);
    const dateRangeSelectorData = <DateRangeSelectorDialogData>{
      maxDate: this.CALENDAR_DEFAULTS.ADD_TWO_YEARS,
      beginDate: expectedCloseFilter?.start ?? this.CALENDAR_DEFAULTS.NOW,
      endDate: expectedCloseFilter?.end ?? this.CALENDAR_DEFAULTS.ADD_ONE_WEEK,
    };
    await this.dialogResultsToDateService(dateRangeSelectorData, this.expectedCloseFilterService);
  }

  async openAssignedDateSelector(): Promise<void> {
    const assignedDateFilter = await firstValueFrom(this.assignedDateFilter$);
    const dateRangeSelectorData = <DateRangeSelectorDialogData>{
      maxDate: this.CALENDAR_DEFAULTS.NOW,
      beginDate: assignedDateFilter?.start ?? this.CALENDAR_DEFAULTS.START_OF_MONTH,
      endDate: assignedDateFilter?.end ?? this.CALENDAR_DEFAULTS.NOW,
    };
    await this.dialogResultsToDateService(dateRangeSelectorData, this.assignedDateFilterService);
  }

  async openActualCloseSelector(): Promise<void> {
    const actualCloseFilter = await firstValueFrom(this.actualCloseFilter$);
    const dateRangeSelectorData = <DateRangeSelectorDialogData>{
      maxDate: this.CALENDAR_DEFAULTS.ADD_TWO_YEARS,
      beginDate: actualCloseFilter?.start ?? this.CALENDAR_DEFAULTS.NOW,
      endDate: actualCloseFilter?.end ?? this.CALENDAR_DEFAULTS.ADD_ONE_WEEK,
    };

    await this.dialogResultsToDateService(dateRangeSelectorData, this.actualCloseFilterService);
  }

  async openCreatedDateSelector(): Promise<void> {
    const createdDateFilter = await firstValueFrom(this.createdDateFilter$);
    const dateRangeSelectorData = <DateRangeSelectorDialogData>{
      maxDate: this.CALENDAR_DEFAULTS.NOW,
      beginDate: createdDateFilter?.start ?? this.CALENDAR_DEFAULTS.START_OF_MONTH,
      endDate: createdDateFilter?.end ?? this.CALENDAR_DEFAULTS.NOW,
    };
    await this.dialogResultsToDateService(dateRangeSelectorData, this.createdDateFilterService);
  }

  clearPipelinesFilter(): void {
    this.pipelineMultiSelectService.clearPipelineSelection();
    this.pipelineMultiSelectService.clearPipelineStageSelection();
  }

  clearExpectedCloseFilter(): void {
    this.expectedCloseFilterService.clearSelection();
  }

  clearActualCloseFilter(): void {
    this.actualCloseFilterService.clearSelection();
  }

  clearAssignedDateFilter(): void {
    this.assignedDateFilterService.clearSelection();
  }

  clearCreatedDateFilter(): void {
    this.createdDateFilterService.clearSelection();
  }

  clearAccountGroupFilter(): void {
    this.accountFilterService.clearAccountGroupFilter();
  }

  openSalesTeamSelector(): void {
    this.dialog.open(SalesTeamMultiSelectComponent, { width: '400px' });
  }

  clearSalespeopleFilters(): void {
    this.spStore.setSelectedSalespeople([]);
  }

  clearOpportunityTypeFilter(): void {
    this.opportunityTypeFilterService.clearOpportunityTypeSelection();
  }

  openTagSelector(): void {
    this.dialog.open(TagMultiSelectComponent, { width: '400px' });
  }

  clearSalesTeamFilters(): void {
    this.stStore.setSelectedSalesTeams([]);
  }

  clearTagFilter(): void {
    this.tagStore.clearTagsSelection();
  }

  openPipelineStatusSelector(): void {
    this.dialog.open(PipelineStatusCheckboxComponent, { width: '400px' });
  }

  clearPipelineStatuses(): void {
    this.statusesFilterStore.resetFilter();
  }

  public openPackageSelector(): void {
    this.dialog.open(PipelineFilterPackageSelectorComponent, {
      width: '400px',
    });
  }

  public openProductSelector(): void {
    this.dialog.open(ProductSelectorComponent, {
      width: '400px',
    });
  }

  clearPackageFilter(): void {
    this.pkgStore.clearPackageSelection();
  }

  clearProductsFilter(): void {
    this.productSelectorService.clearProductSelection();
  }

  private buildStatusFitlerChipNames(): Observable<string> {
    const i18nWonKey = 'FRONTEND.SALES_OPPORTUNITIES.WON_FILTER';
    const i18nLostKey = 'FRONTEND.SALES_OPPORTUNITIES.LOST_FILTER';
    const i18nOpenKey = 'FRONTEND.SALES_OPPORTUNITIES.OPEN_FILTER';
    return combineLatest([this.i18n.stream([i18nWonKey, i18nLostKey, i18nOpenKey]), this.selectedStatuses$]).pipe(
      map(([translations, s]: [any, PipelineStatuses]): string => {
        const won = s?.won ? translations[i18nWonKey] : '';
        const lost = s?.lost ? translations[i18nLostKey] : '';
        const open = s?.open ? translations[i18nOpenKey] : '';
        return [won, lost, open].filter(Boolean).join(', ');
      }),
    );
  }

  private async dialogResultsToDateService(
    dateRangeSelectorData: DateRangeSelectorDialogData,
    dateService: DateFilterServiceInterface,
  ): Promise<void> {
    const dialogRef = this.dialog.open(DateRangeSelectorDialogComponent, { data: dateRangeSelectorData });
    await firstValueFrom(
      dialogRef.afterClosed().pipe(
        map((results: DateRangeSelectorDialogData) => {
          if (results != null) {
            dateService.setDateRange(results.beginDate, results.endDate);
          }
        }),
      ),
    );
  }
}
